import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Link,
  Radio,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FacetChips } from './facet-chips';
import { Dot } from './parts';
import { inputSx, plainButtonSx, primaryButtonSx, segmentSx } from './styles';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useDevices } from '@/hooks/use-devices';
import { useShellJobPreview } from '@/hooks/use-shell-jobs';
import { useSoftwareUpdateGroups } from '@/hooks/use-software-update-groups';
import { getAPIErrorMessage } from '@/lib/api';
import { parseDeviceList } from '@/lib/bulk-shell-prefill';
import { dropCheckedLine, listCheckFresh, targetRequest, type DevicesChoice, type ListCheck } from '@/lib/bulk-shell-target';
import { toDeviceFilter } from '@/lib/device-filter-options';
import shellJobService, { type ShellJobTarget } from '@/lib/shell-job.service';
import { SHELL_MONO, toneText } from '@/theme/tokens';

const PREVIEW_LIMIT = 10;
const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
const WHY: Record<string, string> = { not_found: 'not found', not_machine_id: 'not a machine ID', duplicate: 'duplicate' };
const listSx = { mt: 1, border: 1, borderColor: 'divider', borderRadius: '6px', overflow: 'hidden', fontSize: 12 } as const;
const rowSx = { display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, borderBottom: 1, borderColor: 'divider' } as const;
/** A checked line: mark · machine ID · what it matched or why not · online / remove. */
const lineSx = { ...rowSx, display: 'grid', gridTemplateColumns: '14px 120px minmax(0, 1fr) auto', '&:last-of-type': { borderBottom: 0 } } as const;
const ellipsisSx = { color: 'text.secondary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/** A spinner and what the window is waiting for, in its small text. */
function Busy({ label, sx }: { label: string; sx?: object }) {
  return (
    <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, color: 'text.secondary', ...sx }}>
      <CircularProgress size={14} thickness={5} />
      {label}
    </Box>
  );
}

interface Props {
  /** Super admins: the tenant picked in the drawer. */
  tenantId?: string;
  tenantName?: string;
  showOwnership: boolean;
  /** config:read, the access the groups list needs. */
  canPickGroup: boolean;
  value: DevicesChoice;
  onCancel: () => void;
  onUse: (choice: DevicesChoice) => void;
}

/**
 * The Choose devices window. It edits a copy of the drawer's choice and Use
 * hands it back; the drawer mounts it only while open, so every opening starts
 * from the drawer's current choice.
 */
export function ChooseDevicesDialog({ tenantId, tenantName, showOwnership, canPickGroup, value, onCancel, onUse }: Props) {
  const [draft, setDraft] = useState(value);
  const [previewOpen, setPreviewOpen] = useState(false);
  const patch = (p: Partial<DevicesChoice>) => setDraft((d) => ({ ...d, ...p }));
  const groups = useSoftwareUpdateGroups({ enabled: canPickGroup });
  const group = groups.data?.find((g) => g.id === draft.groupId);

  const req = useMemo(() => targetRequest(draft, tenantId ? { tenant_id: tenantId } : {}), [draft, tenantId]);
  // All, Filter and Group count as you choose. A pasted list is checked only
  // when you press Check, so typing never calls the API.
  const autoReq = draft.mode === 'list' ? null : req;
  const debouncedReq = useDebouncedValue(autoReq, 250);
  const preview = useShellJobPreview(debouncedReq);
  const counting = autoReq !== null && (preview.isFetching || autoReq !== debouncedReq);

  const [check, setCheck] = useState<ListCheck | null>(null);
  const checker = useMutation({
    mutationFn: (devices: string[]) => shellJobService.preview({ ...(tenantId ? { tenant_id: tenantId } : {}), target: 'list', devices }),
    onSuccess: (result, devices) => setCheck({ devices, preview: result }),
  });
  const pasted = useMemo(() => (draft.mode === 'list' ? parseDeviceList(draft.listText) : []), [draft.mode, draft.listText]);
  // A check counts only while the text is still the list it covered; Use waits for a fresh one.
  const listFresh = draft.mode === 'list' && listCheckFresh(draft.listText, check?.devices);
  const checkList = () => {
    if (pasted.length > 0) checker.mutate(pasted);
  };
  // The list the window opens with was chosen before: check it once, so its lines show.
  useEffect(() => {
    const devices = value.mode === 'list' ? parseDeviceList(value.listText) : [];
    if (devices.length > 0) checker.mutate(devices);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, for the choice the window opened with
  }, []);

  // Never another tab's numbers, nor numbers for a request still on its way.
  const data = draft.mode === 'list' ? (listFresh ? check?.preview : undefined) : autoReq && !counting ? preview.data : undefined;
  const total = data?.total ?? 0;
  const online = data?.online ?? 0;
  const offline = (data?.offline ?? 0) + (data?.not_enrolled ?? 0);
  const notUsed = draft.mode === 'list' ? (data?.lines ?? []).filter((l) => l.result !== 'matched').length : 0;

  // Preview: the first devices the run would reach. A pasted list shows its own lines; a group, its members.
  const previewFilter = useMemo(() => {
    if (!debouncedReq || debouncedReq.target === 'list' || debouncedReq.target === 'group') return undefined;
    const f = debouncedReq.target === 'filter' ? (debouncedReq.filter ?? {}) : {};
    return { ...toDeviceFilter(f, debouncedReq.tenant_id), limit: PREVIEW_LIMIT };
  }, [debouncedReq]);
  const showPreview = previewOpen && draft.mode !== 'list';
  const previewDevices = useDevices(previewFilter, showPreview && !!previewFilter);
  const previewRows =
    draft.mode === 'group'
      ? (group?.members ?? []).slice(0, PREVIEW_LIMIT).map((m) => ({ id: m.device_id, machineId: m.machine_id ?? '', number: m.number, serial: m.serial_number, status: undefined as string | undefined }))
      : (previewDevices.data?.devices ?? []).map((d) => ({ id: d.id, machineId: d.machine_id, number: d.number, serial: d.serial_number, status: d.status as string | undefined }));
  const previewTotal = draft.mode === 'group' ? (group?.members.length ?? 0) : total;
  const previewLoading = draft.mode !== 'group' && previewDevices.isLoading;
  const previewFailed = draft.mode !== 'group' && previewDevices.isError;

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth={false}
      aria-labelledby="choose-devices-title"
      PaperProps={{
        sx: {
          width: 'min(640px, calc(100vw - 32px))',
          m: 2,
          p: 2.5,
          borderRadius: '8px',
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 12px 32px rgba(0,0,0,.25)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography component="h2" id="choose-devices-title" sx={{ fontSize: 16, fontWeight: 700 }}>
          Choose devices
        </Typography>
        <Button variant="outlined" aria-label="Close" onClick={onCancel} sx={plainButtonSx}>
          ✕
        </Button>
      </Box>
      <ToggleButtonGroup aria-label="Devices" exclusive value={draft.mode} onChange={(_, m: ShellJobTarget | null) => m && patch({ mode: m })} sx={{ ...segmentSx, alignSelf: 'flex-start' }}>
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="filter">Filter</ToggleButton>
        {canPickGroup && <ToggleButton value="group">Group</ToggleButton>}
        <ToggleButton value="list">Paste list</ToggleButton>
      </ToggleButtonGroup>

      {draft.mode === 'all' && <Typography sx={{ fontSize: 13 }}>Every device in {tenantName ?? 'this tenant'}.</Typography>}
      {draft.mode === 'filter' && (
        <FacetChips
          value={draft.filter}
          onChange={(u) => setDraft((d) => ({ ...d, filter: typeof u === 'function' ? u(d.filter) : u }))}
          tenantId={tenantId}
          showOwnership={showOwnership}
        />
      )}
      {draft.mode === 'group' && groups.isLoading && <Busy label="Loading groups…" sx={{ py: 1 }} />}
      {draft.mode === 'group' && groups.isError && (
        <Alert severity="error">{getAPIErrorMessage(groups.error, 'Could not load groups.')}</Alert>
      )}
      {draft.mode === 'group' && groups.data && groups.data.length === 0 && (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No software update groups yet.</Typography>
      )}
      {draft.mode === 'group' && groups.data && groups.data.length > 0 && (
        <Box role="radiogroup" aria-label="Group" sx={{ border: 1, borderColor: 'divider', borderRadius: '6px', maxHeight: 240, overflowY: 'auto' }}>
          {groups.data.map((g) => (
            <Box
              key={g.id}
              component="label"
              sx={{
                display: 'grid',
                gridTemplateColumns: '18px minmax(0, 1fr) auto',
                gap: 1,
                alignItems: 'center',
                px: 1.25,
                py: 1,
                cursor: 'pointer',
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 0 },
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Radio size="small" checked={draft.groupId === g.id} onChange={() => patch({ groupId: g.id })} sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{g.name}</Typography>
                {g.description && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{g.description}</Typography>}
              </Box>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{plural(g.members.length, 'device')}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {draft.mode === 'list' && (
        <TextField
          multiline
          fullWidth
          minRows={5}
          maxRows={10}
          value={draft.listText}
          onChange={(e) => patch({ listText: e.target.value })}
          placeholder="Machine IDs, one per line"
          sx={{ ...inputSx, '& .MuiInputBase-root': { ...inputSx['& .MuiInputBase-root'], p: '6px 10px' } }}
          slotProps={{ htmlInput: { 'aria-label': 'Machine IDs', style: { fontFamily: SHELL_MONO, fontSize: 12 } } }}
        />
      )}

      {req === null ? (
        // Groups still loading (or failed): the spinner or error above says enough.
        draft.mode === 'group' && !groups.data ? null : (
          <Typography sx={{ mt: '10px', fontSize: 12, color: 'text.secondary' }}>
            {draft.mode === 'group' ? 'Pick a group.' : 'Paste at least one machine ID.'}
          </Typography>
        )
      ) : draft.mode === 'list' && !listFresh ? (
        <Box sx={{ mt: '10px', display: 'flex', alignItems: 'center', gap: 1, fontSize: 12 }}>
          {checker.isError && !checker.isPending ? (
            <Box component="span" sx={{ color: toneText('error') }}>
              {getAPIErrorMessage(checker.error, 'Could not check these devices.')}
            </Box>
          ) : (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {checker.isPending
                ? `Checking ${plural(pasted.length, 'line')}…`
                : check
                  ? 'The list changed. Press Check again.'
                  : `${plural(pasted.length, 'line')} · not checked yet`}
            </Box>
          )}
          <CheckButton pending={checker.isPending} onClick={checkList} />
        </Box>
      ) : draft.mode !== 'list' && preview.isError && !counting ? (
        <Alert severity="error" sx={{ mt: '10px' }}>
          {getAPIErrorMessage(preview.error, 'Could not count these devices.')}
        </Alert>
      ) : data ? (
        <Box sx={{ mt: '10px', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: 12 }}>
          <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {plural(total, 'device')}
          </Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {online.toLocaleString()} online · {offline.toLocaleString()} offline
          </Box>
          {notUsed > 0 && (
            <Box component="span" sx={{ color: toneText('error') }}>
              {notUsed.toLocaleString()} not used
            </Box>
          )}
          {draft.mode !== 'list' && total > 0 && (
            <Link
              component="button"
              type="button"
              underline="hover"
              aria-expanded={showPreview}
              onClick={() => setPreviewOpen((v) => !v)}
              sx={{ fontSize: 12, color: toneText('info') }}
            >
              {showPreview ? 'Hide preview' : 'Preview'}
            </Link>
          )}
          {draft.mode === 'list' && <CheckButton pending={checker.isPending} onClick={checkList} />}
        </Box>
      ) : (
        <Busy label="Counting devices…" sx={{ mt: '10px' }} />
      )}

      {draft.mode === 'list' && data?.lines && data.lines.length > 0 && (
        <Box sx={{ ...listSx, maxHeight: 220, overflowY: 'auto' }}>
          {data.lines.map((l, i) => (
            <Box key={`${i}-${l.input}`} sx={lineSx}>
              {l.result === 'matched' ? (
                <>
                  <Box component="span" sx={{ color: toneText('success') }}>✓</Box>
                  <Box component="span" sx={{ fontFamily: SHELL_MONO }}>{l.input}</Box>
                  <Box component="span" sx={ellipsisSx}>
                    {[l.device_number, l.serial_number && `SN ${l.serial_number}`].filter(Boolean).join(' · ')}
                  </Box>
                  <Box component="span" sx={{ color: l.status === 'pending' ? 'text.primary' : 'text.secondary' }}>
                    {l.status === 'pending' ? 'online' : 'offline'}
                  </Box>
                </>
              ) : (
                <>
                  <Box component="span" sx={{ color: toneText('error') }}>✗</Box>
                  <Box component="span" sx={{ fontFamily: SHELL_MONO }}>{l.input}</Box>
                  <Box component="span" sx={{ color: toneText('error') }}>{WHY[l.result]}</Box>
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => {
                      // An unmatched line never counted: drop it from the check too, no new check needed.
                      patch({ listText: parseDeviceList(draft.listText).filter((x) => x !== l.input).join('\n') });
                      setCheck((c) => c && dropCheckedLine(c, l.input));
                    }}
                    sx={{ fontSize: 12, color: toneText('info') }}
                  >
                    remove
                  </Link>
                </>
              )}
            </Box>
          ))}
        </Box>
      )}

      {showPreview && (
        <Box sx={listSx}>
          {previewLoading ? (
            <Busy label="Loading devices…" sx={{ px: 1.25, py: 0.75 }} />
          ) : previewFailed ? (
            <Box sx={{ px: 1.25, py: 0.75, color: toneText('error') }}>Could not load the devices.</Box>
          ) : (
            previewRows.map((d) => (
              <Box key={d.id} sx={rowSx}>
                <Dot tone={d.status === 'online' ? 'success' : 'neutral'} />
                <Box component="span" sx={{ fontFamily: SHELL_MONO }}>{d.machineId || d.number}</Box>
                <Box component="span" sx={ellipsisSx}>
                  {[d.number, d.serial && `SN ${d.serial}`].filter(Boolean).join(' · ')}
                </Box>
                {d.status && (
                  <Box component="span" sx={{ ml: 'auto', color: 'text.secondary' }}>
                    {d.status}
                  </Box>
                )}
              </Box>
            ))
          )}
          <Box sx={{ px: 1.25, py: 0.75, color: 'text.secondary' }}>
            {previewTotal > PREVIEW_LIMIT ? `and ${(previewTotal - PREVIEW_LIMIT).toLocaleString()} more` : `All ${plural(previewTotal, 'device')}`}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button variant="outlined" onClick={onCancel} sx={plainButtonSx}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!data || total === 0} onClick={() => onUse(draft)} sx={primaryButtonSx}>
          {/* No count until one is fresh: "Use 0 devices" would read as a result. */}
          {data ? `Use ${plural(total, 'device')}` : 'Use devices'}
        </Button>
      </Box>
    </Dialog>
  );
}

/** Checks the pasted list now; a spinner while the check runs. */
function CheckButton({ pending, onClick }: { pending: boolean; onClick: () => void }) {
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      disabled={pending}
      startIcon={pending ? <CircularProgress size={12} thickness={5} /> : undefined}
      sx={{ ...plainButtonSx, ml: 'auto', flex: 'none' }}
    >
      {pending ? 'Checking…' : 'Check'}
    </Button>
  );
}
